import { Api } from "laravel-request";
import { clearChatId } from "#app/api/middleware/websocketConnector";
import { TaskContactDTO } from "#app/api/dto/contactDTO";

import { makeRequest } from "#app/components/Cabinet/Chat/ChatRequests";

import {
    CallbackOptions,
    ClientRatingUpdateParams,
    PossiblyBooleanApiParam,
    SortIdCases,
} from "#app/api/types";

export const CLIENT_RELATIONS = ["atonUsers"] as const;

export const MULTIPLE_MANAGERS = ["multiple_managers", "multiple_managers_pivot.manager"] as const;

export const FINANCE_GROUP_RELATIONS = [
    "financeGroups",
    "financeGroups.unionUser",
    "oppositeFinanceGroup",
] as const;

export const MAIN_CONTACT_RELATIONS = (contactQuery: any) =>
    contactQuery
        .with("refuseReason")
        .with("tasks", (tasksQuery: any) => tasksQuery.orderBy("end_at", "DESC"));

export const buildFetchItemsQuery = (
    clientId: string | number | null,
    isArchive: PossiblyBooleanApiParam,
    sortId: SortIdCases | null,
    isFullGroup: PossiblyBooleanApiParam = false
) => {
    const query = Api.get("client", "index")
        .with(...CLIENT_RELATIONS)
        .with("multiple_managers_pivot.manager")
        .with("mainContact", MAIN_CONTACT_RELATIONS)
        .with("multiple_managers");
    if (isFullGroup) query.with(...FINANCE_GROUP_RELATIONS);

    query.whereHas("mainContact", (contactQuery: any) =>
        contactQuery.where("is_archive", Number(Boolean(isArchive)))
    );

    if (clientId) query.where("id", clientId);

    return query;
};

export const getClientTransitions = (callbackOnSuccess: any) => {
    return Api.get("crm-client-transition", "index").all((response) => {
        callbackOnSuccess && callbackOnSuccess(response);
    });
};

export const callbackOnFetchItemsSuccess = ({
    response,
    userData,
    actions,
}: CallbackOptions): void => {
    const { appendLastMessages, sortItems, setLoading } = actions;
    const clients = response.data || [];
    const phones = clients
        .filter((client: any) => client.mainContact?.phone)
        .map((client: any) => clearChatId(client.mainContact.phone));

    makeRequest({
        url: "chat/last",
        data: { phones },
        userId: userData?.id,
        callback: (lastMessages: any) => {
            const updatedClients = appendLastMessages(lastMessages, [...clients]);
            sortItems(updatedClients);
            setLoading(false);
        },
        errorCallback: () => {
            sortItems([...clients]);
            setLoading(false);
        },
    });
};

export const getTaskClients = (
    userId: string | number,
    onResult: (contacts: TaskContactDTO[]) => void
) => {
    Api.get("client", "index")
        .where("manager_id", userId)
        .all((clientResponse) => {
            Api.get("crm-contact", "index")
                .where("user_id", userId)
                .all((contactResponse) => {
                    const taskContacts = [
                        ...TaskContactDTO.fromArray(clientResponse.data, "user"),
                        ...TaskContactDTO.fromArray(contactResponse.data, "contact"),
                    ];
                    onResult(taskContacts);
                });
        });
};

export const getClientsWithRelations = (callbackOnSuccess: any, callbackOnError?: any) => {
    Api.get("client", "index")
        .with(...CLIENT_RELATIONS)
        .with("mainContact", MAIN_CONTACT_RELATIONS)
        .with(...FINANCE_GROUP_RELATIONS)
        .with(...MULTIPLE_MANAGERS)
        .all(
            (response) => callbackOnSuccess && callbackOnSuccess(response),
            (error) => callbackOnError && callbackOnError(error)
        );
};

export const getUserFinanceGroup = (userId: any, responseCallback: (response: any) => any) => {
    Api.get("user-finance-group", "index")
        .with("unionUser")
        .where("user_id", userId)
        .all(({ data }) => {
            responseCallback(data);
        });
};

export const putUpdateClientField = (
    clientId: any,
    params: ClientRatingUpdateParams,
    responseCallback: (response: any) => any,
    errorCallback?: (error: any) => any
) => {
    Api.putArg("client", "update-field", clientId, params)
        .withoutNotify()
        .call(
            (response) => response && responseCallback && responseCallback(response),
            (error) => error && errorCallback && errorCallback(error)
        );
};
